import React, { useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Spin } from 'antd';
import { useTranslator } from 'tools/view-hooks/translator-hook';

interface ReceptionScannerProps {
    onDecode: (token: string) => void;
    onPermissionError: () => void;
    // false mientras corre la verificación: pinta el velo y bloquea decodes.
    armed: boolean;
}

const SCANNER_ID = 'reception-scanner';

export const ReceptionScanner: React.FC<ReceptionScannerProps> = ({ onDecode, onPermissionError, armed }) => {
    const { translate } = useTranslator();
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const handledRef = useRef(false);

    // Volver a 'scanner' desde 'verifying' NO remonta este componente (el
    // fragment renderiza el mismo elemento en los dos stages), así que sin este
    // re-armado handledRef queda en true para siempre y el escáner enmudece.
    useEffect(() => {
        if (armed) {
            handledRef.current = false;
        }
    }, [armed]);

    useEffect(() => {
        const scanner = new Html5Qrcode(SCANNER_ID);
        scannerRef.current = scanner;
        scanner
            .start(
                { facingMode: 'environment' },
                { fps: 10, qrbox: 250 },
                (decodedText) => {
                    if (handledRef.current) {
                        return;
                    }
                    handledRef.current = true;
                    onDecode(decodedText);
                },
                undefined,
            )
            .catch(() => onPermissionError());

        return () => {
            const s = scannerRef.current;
            if (s) {
                try {
                    s.stop()
                        .then(() => s.clear())
                        .catch(() => undefined);
                } catch {
                    // html5-qrcode tira SINCRÓNICAMENTE ("Cannot stop, scanner is not
                    // running or paused") si nunca llegó a arrancar — el caso de cámara
                    // denegada. Sin esta guarda la excepción escapa del cleanup y React
                    // desmonta el árbol entero: pantalla en blanco en vez del stage de
                    // permiso. No hay nada que detener.
                }
            }
        };
        // eslint-disable-next-line
    }, []);

    return (
        <div className={'reception-scanview'}>
            <div className={'reception-camera'}>
                <div id={SCANNER_ID} />
                {!armed && (
                    <div className={'reception-verifying'}>
                        <Spin size={'large'} />
                        <span>{translate({ key: 'reception.verifying' })}</span>
                    </div>
                )}
            </div>
            <div className={'reception-hint'}>{translate({ key: 'reception.scan-hint' })}</div>
        </div>
    );
};
